/**
 * The first sentence of the product is a greeting, and it used to be the
 * literal "Boa tarde" — wrong for two thirds of the working day (audit P-02).
 *
 * The hour that matters is the clinic's, not the browser's: a receptionist on
 * a laptop that never left another timezone is still opening the clinic at
 * eight in the morning. The timezone comes from the clinic's settings
 * (`GET /get-settings`). While that read has not answered, or answered with a
 * zone this browser cannot resolve, the greeting says "Olá": a time of day
 * nobody measured is not a time of day (PRODUCT_GOVERNANCE rule 8, no silent
 * fallback — the device clock is not a stand-in for the clinic's).
 */

/** Used when the clinic's hour is unknown. Claims nothing about the day. */
export const NEUTRAL_GREETING = "Olá";

export type Greeting = "Bom dia" | "Boa tarde" | "Boa noite" | typeof NEUTRAL_GREETING;

/** Brazilian day split: morning until noon, afternoon until 18h, night after. */
export function greetingForHour(hour: number): Greeting {
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
}

/**
 * Hour 0-23 at `timeZone`, or null when the browser does not know the zone
 * (`Intl` throws a RangeError on an unknown identifier).
 */
export function hourInTimeZone(now: Date, timeZone: string): number | null {
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone,
    }).format(now);
    const hour = Number.parseInt(formatted, 10);
    return Number.isNaN(hour) ? null : hour;
  } catch {
    return null;
  }
}

export function clinicGreeting(
  timeZone: string | null | undefined,
  now: Date = new Date()
): Greeting {
  if (!timeZone) return NEUTRAL_GREETING;
  const hour = hourInTimeZone(now, timeZone);
  return hour === null ? NEUTRAL_GREETING : greetingForHour(hour);
}
